import { useState } from "react";
import DeferredLeadForm from "@/components/DeferredLeadForm";
import BookCallFlow from "@/components/BookCallFlow";
import { useSetting } from "@/utils/settingsService";

const STEPS = [
  { n: "01", title: "Understand", desc: "Discovery call to map team requirement and business goal." },
  { n: "02", title: "Map", desc: "Identify skill gaps and translate them into outcomes." },
  { n: "03", title: "Customize", desc: "Build the curriculum around roles and tools you actually use." },
  { n: "04", title: "Deliver", desc: "Live / corporate training with structured assignments." },
  { n: "05", title: "Measure", desc: "Share outcomes, feedback, and next-step recommendations." },
];

const USECASES = [
  "Upskill SAP teams",
  "Train Workday implementation / support",
  "Run AI adoption workshops",
  "Roll out Copilot or Claude across departments",
  "Train freshers or lateral hires",
  "Prepare teams for new projects",
  "Leadership AI awareness programs",
];

export default function CorporateTrainingPreview({
  formName = "home-page-corporate-training-enquiry-form",
  // Plain badge by default (used standalone on the Corporate Training
  // page); the homepage overrides this to the numbered "05 · ..." form to
  // match its other sections' sequence.
  badgeLabel = "Corporate training",
}) {
  const visible = useSetting("corporate_cta_visible", true);
  const [bookCall, setBookCall] = useState({ open: false, prefill: null });
  if (!visible) return null;
  return (
    <section
      id="corporate-preview"
      data-testid="corporate-training-preview"
      className="py-12 lg:py-15 relative overflow-hidden scroll-mt-[120px] lg:scroll-mt-[136px]"
    >
      <div className="absolute inset-0 -z-10 bg-grid opacity-50" aria-hidden />
      <div className="mx-auto max-w-[1440px] px-5 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <span className="section-badge">{badgeLabel}</span>
            <h2 className="font-display text-2xl md:text-3xl lg:text-[34px] font-bold text-ink mt-3 leading-[1.08] text-balance">
              Corporate IT training built around your team&apos;s{" "}
              <span className="text-royal-deep">business goals.</span>
            </h2>
            <p className="mt-5 text-[15.5px] leading-relaxed text-ink-soft max-w-2xl">
              ZaranTech helps organizations upskill teams in SAP, Workday, AI, cloud, productivity
              tools, and enterprise applications through customized training programs.
            </p>
          </div>
        </div>

        {/* Process flow */}
        <div className="mt-12 relative">
          {/* Desktop timeline */}
          <div className="hidden md:grid grid-cols-5 gap-4 relative">
            <div
              className="absolute left-[10%] right-[10%] top-9 h-px"
              style={{
                background:
                  "linear-gradient(90deg, var(--royal) 0%, var(--cyan) 50%, var(--royal) 100%)",
              }}
              aria-hidden
            />
            {STEPS.map((s) => (
              <div key={s.n} className="group relative">
                <div className="mx-auto relative z-10 h-[72px] w-[72px] rounded-full bg-white border-2 border-royal-deep flex items-center justify-center font-display text-lg font-bold text-royal-deep transition-colors duration-200 group-hover:bg-[var(--royal-deep)] group-hover:text-white">
                  {s.n}
                </div>
                <h3 className="text-center font-display text-[15.5px] font-bold text-ink mt-4">
                  {s.title}
                </h3>
                <p className="text-center text-[12.5px] text-ink-soft mt-1.5 px-3 leading-snug">
                  {s.desc}
                </p>
              </div>
            ))}
          </div>

          {/* Mobile vertical timeline */}
          <div className="md:hidden space-y-5 relative">
            <div
              className="absolute left-[27px] top-3 bottom-3 w-px bg-line"
              aria-hidden
            />
            {STEPS.map((s) => (
              <div key={s.n} className="group flex gap-4">
                <div className="relative z-10 h-14 w-14 shrink-0 rounded-full bg-white border-2 border-royal-deep flex items-center justify-center font-display text-sm font-bold text-royal-deep transition-colors duration-200 group-hover:bg-[var(--royal-deep)] group-hover:text-white">
                  {s.n}
                </div>
                <div className="pt-2">
                  <h3 className="font-display text-base font-bold text-ink">{s.title}</h3>
                  <p className="text-sm text-ink-soft mt-1">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Lower split: use cases + form */}
        <div className="mt-16 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.22em] text-ink-soft mb-4">
              Common engagements
            </p>
            <ul className="space-y-2">
              {USECASES.map((u) => (
                <li
                  key={u}
                  className="flex items-start gap-3 rounded-lg border border-line bg-white px-4 py-3 text-[14px] text-ink hover:border-royal/40 transition-colors"
                >
                  <span
                    className="mt-2 h-1 w-3 rounded-full"
                    style={{ background: "var(--cyan-deep)" }}
                    aria-hidden
                  />
                  {u}
                </li>
              ))}
            </ul>
          </div>

          <div id="corporate-preview-form" className="lg:col-span-7 rounded-2xl border border-line bg-white p-6 lg:p-8 relative scroll-mt-[120px] lg:scroll-mt-[136px]">
            <p className="font-mono-ui text-[11px] uppercase tracking-[0.22em] text-royal">
              Request corporate training
            </p>
            <h3 className="font-display text-2xl font-bold text-ink mt-2 leading-snug">
              Tell us about your team. We&apos;ll come back with a tailored proposal.
            </h3>
            <div className="mt-6">
              <DeferredLeadForm
                variant="corporate"
                formName={formName}
                showCourseField
                minHeight={470}
                onSuccess={({ prefill }) => setBookCall({ open: true, prefill })}
              />
            </div>
          </div>
        </div>
      </div>

      <BookCallFlow
        open={bookCall.open}
        prefill={bookCall.prefill}
        onClose={() => setBookCall({ open: false, prefill: null })}
      />
    </section>
  );
}
